import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { riddledInDebug } from './debug/riddledInDebug';

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/riddled-in-image`;
};

const buildAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };

  // Keep parity with other function callers: send apikey if present.
  const anon = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);
  const jwtAnon = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const apikeyToSend = jwtAnon || anon || null;

  const accessToken = await getValidAccessToken();
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (apikeyToSend) {
    headers.apikey = apikeyToSend;
  }

  return headers;
};

const parseResponseBody = async (response) => {
  const text = await response.text();
  let dataJson = null;
  try {
    dataJson = text ? JSON.parse(text) : null;
  } catch {
    dataJson = null;
  }
  return { text, dataJson };
};

export const listRiddledInImages = async ({ gameId }) => {
  riddledInDebug('images:list:start', { gameId: gameId || null });
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    riddledInDebug('images:list:no_auth', { gameId: gameId || null });
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'list', gameId }),
  });

  const { text, dataJson } = await parseResponseBody(response);
  riddledInDebug('images:list:response', {
    status: response.status,
    ok: response.ok,
    code: dataJson?.code || null,
  });

  if (!response.ok) {
    riddledInDebug('images:list:failed', {
      status: response.status,
      code: dataJson?.code || null,
      message: dataJson?.error || text || null,
    });
    const error = new Error(dataJson?.error || text || 'Failed to load images.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  const images = Array.isArray(dataJson?.images) ? dataJson.images : [];
  riddledInDebug('images:list:success', { count: images.length });
  return {
    gameId: dataJson?.gameId || gameId,
    images: images.map((img) => ({
      id: img?.id,
      imageUrl: img?.imageUrl || img?.image_url || img?.url,
      model: img?.model || null,
      createdAt: img?.createdAt || img?.created_at || null,
    })),
  };
};

export const generateRiddledInImage = async ({ gameId }) => {
  riddledInDebug('images:generate:start', { gameId: gameId || null });
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    riddledInDebug('images:generate:no_auth', { gameId: gameId || null });
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'generate', gameId }),
  });

  const { text, dataJson } = await parseResponseBody(response);
  riddledInDebug('images:generate:response', {
    status: response.status,
    ok: response.ok,
    code: dataJson?.code || null,
    requestId: dataJson?.requestId ?? null,
  });

  if (!response.ok) {
    riddledInDebug('images:generate:failed', {
      status: response.status,
      code: dataJson?.code || null,
      message: dataJson?.error || text || null,
    });
    const error = new Error(dataJson?.error || text || 'Image generation failed.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  riddledInDebug('images:generate:success', {
    hasImageUrl: Boolean(dataJson?.imageUrl || dataJson?.image_url),
    cost: dataJson?.cost,
    coins: dataJson?.coins,
    model: dataJson?.model || null,
  });
  return {
    gameId: dataJson?.gameId || gameId,
    imageUrl: dataJson?.imageUrl || dataJson?.image_url || null,
    cost: dataJson?.cost,
    coins: dataJson?.coins,
    model: dataJson?.model,
    requestId: dataJson?.requestId ?? null,
  };
};

